import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Lock, Shield, Key, Server, Users, Building2, FileCheck, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function ConformitaSicurezza() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Conformità e Sicurezza"
        description="Protezione dati allineata al GDPR, autenticazione sicura e accesso crittografato. TED Monitor è progettato per conformità e sicurezza nel monitoraggio appalti UE."
        path="/it/conformita-sicurezza"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/conformita-sicurezza" },
          { lang: "en", href: "https://tedmonitor.eu/compliance-and-security" },
          { lang: "x-default", href: "https://tedmonitor.eu/compliance-and-security" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Conformità e sicurezza per il monitoraggio appalti UE
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              I workflow di procurement pubblico spesso coinvolgono informazioni sensibili, attività di monitoraggio
              strutturate e requisiti di utilizzo professionale. TED Monitor è progettato per supportare accesso sicuro,
              gestione privacy-conscious dei dati account e pratiche allineate al GDPR.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi TED
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti europei
              </Link>
              <Link
                to="/it/accesso-multilingue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Accesso multilingue
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>

            <p className="mt-4 text-[11px] text-muted-foreground">
              Fonte ufficiale: TED (Tenders Electronic Daily) – Supplemento alla Gazzetta Ufficiale dell'Unione Europea.
            </p>
          </div>
        </section>

        {/* GDPR */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Protezione dati allineata al GDPR</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor segue principi allineati al GDPR per la gestione dei dati account e di servizio, supportando
              elaborazione privacy-conscious e diritti degli utenti in un contesto di monitoraggio professionale.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Questo include:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Elaborazione allineata ai principi di trasparenza e limitazione delle finalità",
                "Protezione delle informazioni personali dell'account e delle credenziali di accesso",
                "Rispetto dei diritti degli utenti (accesso, rettifica, cancellazione ove applicabile)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              La conformità è trattata come requisito di prodotto—non come un ripensamento.
            </p>

            <p className="mt-4">
              <Link to="/privacy-policy" className="text-primary hover:underline font-medium text-sm">
                Leggi la nostra Privacy Policy →
              </Link>
            </p>
          </div>
        </section>

        {/* Autenticazione */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Key className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Autenticazione sicura e controllo accessi
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              L'accesso alla piattaforma è protetto da meccanismi di autenticazione sicura progettati per ridurre il
              rischio di accesso non autorizzato e mantenere gli account logicamente isolati.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Risultati operativi:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "I dati a livello account rimangono protetti dietro autenticazione",
                "Gli utenti vedono solo informazioni associate al loro contesto account",
                "Le azioni sensibili sono ristrette agli utenti autenticati",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Crittografia / infrastruttura */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Accesso crittografato e infrastruttura sicura
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor utilizza crittografia e pratiche di comunicazione sicura per proteggere l'utilizzo della
              piattaforma e le interazioni sistema-a-sistema.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Lock className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Dati in transito</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Key className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Accesso account</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Server className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Comunicazioni sistema</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              L'infrastruttura è progettata con affidabilità e integrità dei dati in mente per supportare workflow di
              monitoraggio continui.
            </p>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Scopri la fonte dati →
              </Link>
            </p>
          </div>
        </section>

        {/* Uso professionale */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileCheck className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Progettato per requisiti di utilizzo professionale
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Per i team professionali, sicurezza e conformità sono requisiti di base. TED Monitor è costruito per
              supportare:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Accesso security-first per workflow professionali",
                "Documentazione e policy orientate alla conformità",
                "Operatività affidabile per monitoraggio continuo",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground mb-3 text-sm">Questo rende TED Monitor adatto per:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-background rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Aziende</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Consulenti</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <FileCheck className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Studi professionali</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Shield className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Team con workflow sensibili</p>
              </div>
            </div>

            <p className="mt-4">
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="text-primary hover:underline font-medium text-sm"
              >
                Esplora il software appalti europei →
              </Link>
            </p>
          </div>
        </section>

        {/* Fiducia */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Fiducia e conformità nel procurement UE
              </h2>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Nel monitoraggio appalti UE, la fiducia si costruisce attraverso policy trasparenti, accesso sicuro e
              gestione privacy-conscious dei dati account. TED Monitor è progettato per fornire un ambiente sicuro e
              orientato alla conformità per organizzazioni che monitorano opportunità pubbliche in tutta Europa.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Usa una piattaforma procurement sicura e orientata alla conformità
            </h2>
            <p className="text-base mb-6 opacity-90">
              Inizia a monitorare gli appalti pubblici UE con una piattaforma progettata per requisiti di utilizzo
              professionale.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-muted-foreground hover:text-foreground">
                Monitoraggio bandi UE
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-muted-foreground hover:text-foreground">
                Alert bandi
              </Link>
              <Link to="/it/software-appalti-pubblici-europei" className="text-muted-foreground hover:text-foreground">
                Software appalti
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
